import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';

import { IFormSubmission, ILocation, IPaginatedTableResponse, ISortingModel, Pagination, TViewPortPolygon, } from '../../models';
import { EntityFilters, PaginatedApiBaseService } from './paginated-api-base.service';

export interface ISubmitFormOptions {
    poiId?: string;
    taskId?: string;
    teamId?: string;
}

@Injectable({ providedIn: 'root' })
export class FormSubmissionApiService extends PaginatedApiBaseService {
    private readonly baseUrl = '/formsubmissions';

    constructor() {
        super('formApi');
    }

    public getFormSubmissions(): Observable<Array<IFormSubmission>> {
        const defaultSorting: ISortingModel<IFormSubmission> = {
            property: 'timestamp',
            direction: 'descending',
            order: 1,
        };

        return this.getApiUrl(this.baseUrl).pipe(
            this.paginatedHttpGet<IFormSubmission, IFormSubmission>(null, null, [defaultSorting], null, null, null),
            map((response) => response.data)
        );
    }

    public getFormSubmissionsPaginated<TableModel>(
        pagination: Pagination,
        filters?: EntityFilters,
        sorting?: Array<ISortingModel<TableModel>>,
        filterPreset?: string,
        viewport?: TViewPortPolygon,
        includeArchived = false
    ): Observable<IPaginatedTableResponse<IFormSubmission>> {
        const params = FormSubmissionApiService.getInclusionParams({ includeArchived });
        return this.getApiUrl(this.baseUrl).pipe(
            this.paginatedHttpGet<IFormSubmission, TableModel>(
                pagination,
                filters,
                sorting,
                filterPreset,
                viewport,
                params
            )
        );
    }

    public getFormSubmissionFilters<TFilterModel>(): Observable<TFilterModel> {
        return this.getApiUrl(`${this.baseUrl}/filters`).pipe(mergeMap((url) => this.http.get<TFilterModel>(url)));
    }

    public getFormSubmission(formSubmissionId: string): Observable<IFormSubmission> {
        return this.getApiUrl(`${this.baseUrl}/${formSubmissionId}`).pipe(
            mergeMap((url) => this.http.get<IFormSubmission>(url))
        );
    }

    public submitForm(
        formId: string,
        result: string,
        location: ILocation,
        options?: ISubmitFormOptions
    ): Observable<IFormSubmission> {
        return this.getApiUrl(`${this.baseUrl}`).pipe(
            mergeMap((url) =>
                this.http.post<IFormSubmission>(url, {
                    formId,
                    result,
                    location,
                    poiId: options?.poiId,
                    taskId: options?.taskId,
                    teamId: options?.teamId,
                })
            )
        );
    }

    public getFormSubmissionPdf(
        formSubmissionId: string,
        fileName: string,
        exportDefinitionId?: string
    ): Observable<File> {
        return this.getApiUrl(`${this.baseUrl}/${formSubmissionId}/export/pdf`).pipe(
            switchMap((url) =>
                this.http.post(
                    url,
                    {
                        fileName,
                        exportDefinitionId,
                    },
                    { responseType: 'blob' }
                )
            ),
            map((blob) => new File([blob], fileName, { type: 'application/pdf' }))
        );
    }

    public uploadFile(
        files: Array<File>,
        formId: string,
        question: string
    ): Observable<Array<{ id: string, url: string, contentType: string }>> {
        const formData = new FormData();

        files.forEach(file => {
            formData.append('file', file);
        });

        formData.append("formId", formId);
        formData.append("question", question);

        return this.getApiUrl(`${this.baseUrl}/files`).pipe(mergeMap((url) => this.http.post<Array<{ id: string, url: string, contentType: string }>>(url, formData)));
    }

    private static getInclusionParams(options: { includeArchived?: boolean }): HttpParams {
        let params = new HttpParams();
        if (options.includeArchived) {
            params = params.append('includeArchived', 'true');
        }

        return params;
    }
}
